import { Badge } from '@/components/ui/badge'
import { RoomPaymentLabel } from '@/components/room/room-payment-label'
import { Button } from '@/components/ui/button'
import { buildInviteUrl } from '@/lib/arena'
import { useI18n } from '@/lib/i18n'
import { statusI18nSegment } from '@/lib/room-status-label'
import { formatGameSettingBoardLabel } from '@/lib/play'
import { cn } from '@/lib/utils'
import { DoorOpen, ExternalLink, Gamepad2, Users } from 'lucide-react'

import { useSocialText } from '@/social/use-social-text'
import type { SocialRoomProjection } from '@/social/types'

const STATUS_STYLES: Record<SocialRoomProjection['status'], string> = {
  lobby:
    'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  playing: 'border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300',
  completed: 'border-muted-foreground/30 bg-muted text-muted-foreground',
  cancelled: 'border-destructive/30 bg-destructive/10 text-destructive',
  unavailable: 'border-muted-foreground/30 bg-muted text-muted-foreground',
}

export function RoomInviteCard({
  room,
  className,
}: {
  room: SocialRoomProjection | null
  className?: string
}) {
  const text = useSocialText()
  const { t } = useI18n()
  const status = room?.status ?? 'unavailable'
  const unavailable = !room || room.cta === 'unavailable'
  const statusLabel = text(
    `social.room.status.${statusI18nSegment(status)}`,
    {
      lobby: 'Waiting for players',
      playing: 'In progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
      unavailable: 'Unavailable',
    }[status],
  )
  const setting = room?.gameSetting
    ? formatGameSettingBoardLabel(
        room.gameSetting,
        room.language ?? undefined,
        t,
      )
    : null

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border bg-background/80 text-left shadow-sm',
        className,
      )}
      data-testid="social-room-invite-card"
    >
      <div className="flex items-center justify-between gap-3 border-b bg-muted/30 px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Gamepad2 className="size-4" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">
              {text('social.room.title', 'Mentiss multiplayer room')}
            </p>
            {room?.roomCode && (
              <p className="truncate font-mono text-[10px] text-muted-foreground">
                {room.roomCode}
              </p>
            )}
          </div>
        </div>
        <Badge
          variant="outline"
          className={cn('shrink-0 text-[10px]', STATUS_STYLES[status])}
        >
          {statusLabel}
        </Badge>
      </div>

      <div className="space-y-2 p-3 text-xs">
        <RoomPaymentLabel
          freeGame={room?.freeGame}
          paymentMode={room?.paymentMode}
        />
        {room?.language && (
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground">
              {text('social.room.language', 'Language')}
            </span>
            <span className="font-medium uppercase">{room.language}</span>
          </div>
        )}
        {setting && (
          <div className="flex items-start justify-between gap-3">
            <span className="shrink-0 text-muted-foreground">
              {text('social.room.setting', 'Game')}
            </span>
            <span className="line-clamp-2 text-right font-medium">
              {setting}
            </span>
          </div>
        )}
        {typeof room?.humanCount === 'number'
          && typeof room?.capacity === 'number' && (
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1 text-muted-foreground">
              <Users className="size-3.5" />
              {text('social.room.players', 'Players')}
            </span>
            <span className="font-medium">
              {room.humanCount}
              {' / '}
              {room.capacity}
            </span>
          </div>
        )}
      </div>

      <div className="px-3 pb-3">
        {unavailable ? (
          <Button className="w-full" size="sm" disabled>
            {text('social.room.unavailable', 'Room unavailable')}
          </Button>
        ) : (
          <Button className="w-full" size="sm" asChild>
            <a href={buildInviteUrl(room.roomCode)}>
              {room.cta === 'join' ? (
                <DoorOpen className="mr-2 size-4" />
              ) : (
                <ExternalLink className="mr-2 size-4" />
              )}
              {room.cta === 'join'
                ? text('social.room.join', 'Join room')
                : text('social.room.open', 'Open game')}
            </a>
          </Button>
        )}
      </div>
    </div>
  )
}
